"use client";

import { useConsent } from "@packages/consent";
import {
  getDeviceType,
  getDeviceUserAgent,
  getFullName,
  useRequestMe,
} from "@packages/sdk";
import type { LDContext } from "launchdarkly-react-client-sdk";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import { useEffect, useMemo, useState } from "react";

import { abContext } from "../lib";
import { getSearchParamsLDAttributes } from "../utils";

type LDContexts = Partial<Record<"device" | "guest" | "user", LDContext>>;

export type ABContextProviderProps = Omit<
  ComponentProps<typeof abContext.Provider>,
  "value"
>;

export const ABContextProvider = ({
  children,
  ...props
}: ABContextProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Keep track of what contexts have been identified
   * ldClient.identify() requires explicit contexts
   */
  const [seenAsDevice, setSeenAsDevice] = useState(false);
  const [seenAsGuest, setSeenAsGuest] = useState(false);
  const [seenAsUser, setSeenAsUser] = useState(false);

  const searchParams = useSearchParams();

  const ldClient = useLDClient();

  const {
    ajsAnonymousId,
    isConsenting,
    isLoading: isLoadingConsent,
    setAjsAnonymousId,
  } = useConsent();
  const { query: queryMe } = useRequestMe();

  const ldContexts = useMemo<LDContexts>(() => {
    const contexts: LDContexts = {};
    const ldContextAttributesGlobal = {
      platform: "web",
    };
    const isWindowExists = typeof window !== "undefined";

    /**
     * Without consent, LD may initialize before Segment
     * In this case, ONLY create a LD anonymous device context
     * Which will auto-generate a UUID to use as "ajs_anonymous_id" cookie
     * That Segment will use when it initializes
     */
    if (seenAsDevice)
      contexts.device = {
        anonymous: true,
        ...ldContextAttributesGlobal,
      };

    if (isConsenting) {
      const ldContextAttributes: LDContext = {
        anonymous: false,
        apiVersion: process.env.NEXT_PUBLIC_X_HALLOW_VERSION,
        browser: getDeviceUserAgent(),
        deviceType: getDeviceType(),
        preferredLocales: isWindowExists ? window?.navigator?.languages : [],
        screenHeight: isWindowExists ? window?.innerHeight : 0,
        screenWidth: isWindowExists ? window?.innerWidth : 0,
        ...getSearchParamsLDAttributes({ searchParams }),
        ...ldContextAttributesGlobal,
      };

      if (seenAsGuest)
        contexts.guest = {
          key: ajsAnonymousId,
          ...ldContextAttributes,
        };

      if (seenAsUser)
        contexts.user = {
          key: queryMe.data?.id?.toString(),
          ...ldContextAttributes,
          email: queryMe.data?.email,
          firstName: queryMe.data?.name,
          ipCountry: queryMe.data?.country,
          lastName: queryMe.data?.last_name,
          locale: queryMe.data?.language,
          name: getFullName({
            firstName: queryMe.data?.name,
            lastName: queryMe.data?.last_name,
          }),
        };
    }

    return contexts;
  }, [seenAsDevice, seenAsGuest, seenAsUser]);

  /**
   * Determine if any new contexts require identification
   */
  useEffect(() => {
    if (!isLoadingConsent) {
      if (!isConsenting) setSeenAsDevice(true);
      else {
        if (ajsAnonymousId && !seenAsGuest) setSeenAsGuest(true);
        if (queryMe.data?.id && !seenAsUser) setSeenAsUser(true);
      }
    }
  }, [ajsAnonymousId, isConsenting, isLoadingConsent, queryMe.data?.id]);

  useEffect(() => {
    /** Identify all contexts */
    if (Object.keys(ldContexts).length > 0)
      ldClient?.identify({ kind: "multi", ...ldContexts }, null, () => {
        if (isLoading) setIsLoading(false);
      });

    /**
     * If LD initialized before analytics
     * Use LD's auto-generated uuid as "ajs_anonymous_id" cookie
     */
    if (Object.keys(ldContexts).includes("device") && !ajsAnonymousId)
      setAjsAnonymousId(ldClient?.getContext().key as string);
  }, [ldClient, ldContexts]);

  return (
    <abContext.Provider value={{ isLoading }} {...props}>
      {children}
    </abContext.Provider>
  );
};
